import { AgentIcon } from "@promptlayer/ui";
import {
  ArrowRight,
  BarChart3,
  Braces,
  CheckCheck,
  Cog,
  LucideIcon,
  Megaphone,
  Table2,
} from "lucide-react";

import { Badge, BadgeSize, BadgeVariant } from "@/components/ui/Badge";

interface PresetGridProps {
  onSelectModelComparison: () => void;
  onSelectGroundTruth: () => void;
  onSelectStructural: () => void;
  onSelectBrandVisibility?: () => void;
  onSelectGenerateWithAI?: () => void;
  onSelectCustom: () => void;
  onSelectBlank?: () => void;
  includeBlank?: boolean;
  isCreatingBlank?: boolean;
}

interface PresetCard {
  title: string;
  eyebrow: string;
  description: string;
  icon?: LucideIcon;
  useAgentIcon?: boolean;
  action:
    | "model-comparison"
    | "ground-truth"
    | "structural"
    | "brand-visibility"
    | "generate-with-ai"
    | "custom"
    | "blank";
  badge?: string;
  iconClassName: string;
  accentClassName: string;
  haloClassName: string;
  badgeVariant?: BadgeVariant;
  arrowClassName: string;
}

const PRESET_TONES = {
  wrangler: {
    iconClassName: "bg-white text-gray-950 shadow-sm ring-gray-200/80",
    accentClassName: "from-slate-100/70 via-indigo-50/45 to-transparent",
    haloClassName: "bg-indigo-100/45",
    badgeVariant: BadgeVariant.AMBER,
    arrowClassName: "group-hover:text-indigo-400",
  },
  rose: {
    iconClassName: "bg-rose-50/70 text-rose-500 ring-rose-100/70",
    accentClassName: "from-rose-50/80 via-orange-50/40 to-transparent",
    haloClassName: "bg-rose-100/45",
    badgeVariant: BadgeVariant.RED,
    arrowClassName: "group-hover:text-rose-400",
  },
  blue: {
    iconClassName: "bg-blue-50/70 text-blue-500 ring-blue-100/70",
    accentClassName: "from-blue-50/80 via-indigo-50/40 to-transparent",
    haloClassName: "bg-blue-100/45",
    arrowClassName: "group-hover:text-blue-400",
  },
  emerald: {
    iconClassName: "bg-emerald-50/70 text-emerald-500 ring-emerald-100/70",
    accentClassName: "from-emerald-50/80 via-teal-50/40 to-transparent",
    haloClassName: "bg-emerald-100/45",
    arrowClassName: "group-hover:text-emerald-400",
  },
  amber: {
    iconClassName: "bg-amber-50/70 text-amber-600 ring-amber-100/70",
    accentClassName: "from-amber-50/80 via-yellow-50/40 to-transparent",
    haloClassName: "bg-amber-100/45",
    arrowClassName: "group-hover:text-amber-400",
  },
  slate: {
    iconClassName: "bg-slate-50/80 text-slate-600 ring-slate-100/80",
    accentClassName: "from-slate-100/70 via-gray-50/55 to-transparent",
    haloClassName: "bg-slate-100/50",
    arrowClassName: "group-hover:text-slate-400",
  },
} satisfies Record<
  string,
  {
    iconClassName: string;
    accentClassName: string;
    haloClassName: string;
    badgeVariant?: BadgeVariant;
    arrowClassName: string;
  }
>;

const presets: PresetCard[] = [
  {
    title: "Generate with AI",
    eyebrow: "Start with AI",
    description: "Tell Wrangler what you want. It will create a runnable table for you.",
    useAgentIcon: true,
    action: "generate-with-ai",
    badge: "Recommended",
    ...PRESET_TONES.wrangler,
  },
  {
    title: "Brand visibility",
    eyebrow: "Monitor",
    description: "Track when AI answers mention your brand or domain.",
    icon: Megaphone,
    action: "brand-visibility",
    ...PRESET_TONES.rose,
  },
  {
    title: "Model comparison",
    eyebrow: "Compare",
    description: "Compare model outputs, cost, and latency side by side.",
    icon: BarChart3,
    action: "model-comparison",
    ...PRESET_TONES.blue,
  },
  {
    title: "Ground truth check",
    eyebrow: "Validate",
    description: "Compare outputs against trusted answers.",
    icon: CheckCheck,
    action: "ground-truth",
    ...PRESET_TONES.emerald,
  },
  {
    title: "Structure check",
    eyebrow: "Constrain",
    description: "Check that outputs follow your required format.",
    icon: Braces,
    action: "structural",
    ...PRESET_TONES.amber,
  },
  {
    title: "Custom prompt table",
    eyebrow: "Shape yourself",
    description: "Start with prompt inputs and edit freely.",
    icon: Cog,
    action: "custom",
    ...PRESET_TONES.slate,
  },
];

const blankPreset: PresetCard = {
  title: "Blank table",
  eyebrow: "Start fresh",
  description: "Start from a clean table.",
  icon: Table2,
  action: "blank",
  ...PRESET_TONES.slate,
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="space-y-2.5">
    <div className="flex items-center gap-2 px-0.5">
      <div className="h-px flex-1 bg-gradient-to-r from-gray-200 to-transparent" />
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">
        {title}
      </h3>
      <div className="h-px flex-1 bg-gradient-to-l from-gray-200 to-transparent" />
    </div>
    {children}
  </section>
);

const PresetGrid = ({
  onSelectModelComparison,
  onSelectGroundTruth,
  onSelectStructural,
  onSelectBrandVisibility,
  onSelectGenerateWithAI,
  onSelectCustom,
  onSelectBlank,
  includeBlank = false,
  isCreatingBlank = false,
}: PresetGridProps) => {
  const availablePresets = presets.filter(preset => {
    if (preset.action === "brand-visibility") {
      return Boolean(onSelectBrandVisibility);
    }
    if (preset.action === "generate-with-ai") {
      return Boolean(onSelectGenerateWithAI);
    }
    return true;
  });

  const visiblePresets = includeBlank ? [...availablePresets, blankPreset] : availablePresets;
  const aiPreset = visiblePresets.find(preset => preset.action === "generate-with-ai");
  const popularPresets = visiblePresets.filter(preset =>
    ["brand-visibility", "model-comparison", "ground-truth", "structural"].includes(preset.action),
  );
  const buildYourselfPresets = visiblePresets.filter(preset =>
    ["custom", "blank"].includes(preset.action),
  );

  const selectPreset = (action: PresetCard["action"]) => {
    if (action === "ground-truth") {
      onSelectGroundTruth();
      return;
    }
    if (action === "structural") {
      onSelectStructural();
      return;
    }
    if (action === "custom") {
      onSelectCustom();
      return;
    }
    if (action === "brand-visibility") {
      onSelectBrandVisibility?.();
      return;
    }
    if (action === "generate-with-ai") {
      onSelectGenerateWithAI?.();
      return;
    }
    if (action === "blank") {
      onSelectBlank?.();
      return;
    }
    onSelectModelComparison();
  };

  const renderPresetIcon = (preset: PresetCard, className: string) => {
    if (preset.useAgentIcon) {
      return <AgentIcon className={className} />;
    }
    if (preset.icon) {
      const Icon = preset.icon;
      return <Icon className={className} />;
    }
    return null;
  };

  const renderFeaturedCard = (preset: PresetCard) => (
    <button
      key={preset.title}
      type="button"
      onClick={() => selectPreset(preset.action)}
      className="group relative w-full overflow-hidden rounded-[1.35rem] border border-white/80 bg-white p-4 text-left shadow-sm shadow-gray-950/[0.035] ring-1 ring-gray-200/70 transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-lg hover:shadow-gray-950/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900/10"
    >
      <div
        className={`pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-br opacity-90 ${preset.accentClassName}`}
      />
      <div
        className={`pointer-events-none absolute -right-12 -top-12 h-36 w-36 animate-blob-morph rounded-full blur-3xl transition-transform duration-500 group-hover:scale-110 motion-reduce:animate-none ${preset.haloClassName}`}
      />
      <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
        <div className="absolute -left-1/2 top-0 h-full w-1/2 rotate-12 animate-shimmer bg-gradient-to-r from-transparent via-white/35 to-transparent motion-reduce:animate-none" />
      </div>

      <div className="relative flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ring-1 transition-transform duration-300 group-hover:rotate-1 group-hover:scale-105 ${preset.iconClassName}`}
          >
            {renderPresetIcon(preset, "h-[18px] w-[18px]")}
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                {preset.eyebrow}
              </p>
              {preset.badge && (
                <Badge
                  variant={preset.badgeVariant ?? BadgeVariant.GRAY}
                  size={BadgeSize.SM}
                  className={`h-5 px-2 text-[11px] font-medium ${
                    preset.badgeVariant === BadgeVariant.AMBER ? "" : "bg-white/75"
                  }`}
                >
                  {preset.badge}
                </Badge>
              )}
            </div>
            <p className="mt-1 text-base font-semibold tracking-[-0.02em] text-gray-950">
              {preset.title}
            </p>
            <p className="mt-1.5 max-w-xl text-sm leading-5 text-gray-600">{preset.description}</p>
          </div>
        </div>
        <ArrowRight
          className={`mt-1 h-4 w-4 shrink-0 text-gray-300 transition-all duration-300 group-hover:translate-x-1 ${preset.arrowClassName}`}
        />
      </div>
    </button>
  );

  const renderCompactCard = (preset: PresetCard, index: number) => {
    const isBlankLoading = preset.action === "blank" && isCreatingBlank;

    return (
      <button
        key={preset.title}
        type="button"
        onClick={() => selectPreset(preset.action)}
        disabled={isBlankLoading}
        className="group relative min-h-[112px] overflow-hidden rounded-2xl border border-white/80 bg-white p-3 text-left shadow-sm shadow-gray-950/[0.02] ring-1 ring-gray-200/70 transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-md hover:shadow-gray-950/[0.045] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900/10 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-sm"
        style={{ animationDelay: `${Math.min(index, 5) * 35}ms` }}
      >
        <div
          className={`pointer-events-none absolute inset-y-0 left-0 w-28 bg-gradient-to-r opacity-75 transition-opacity duration-300 group-hover:opacity-95 ${preset.accentClassName}`}
        />
        <div
          className={`pointer-events-none absolute -right-10 -top-10 h-24 w-24 rounded-full opacity-0 blur-3xl transition-all duration-500 group-hover:scale-110 group-hover:opacity-80 ${preset.haloClassName}`}
        />

        <div className="relative flex h-full items-start gap-3">
          <div
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ring-1 transition-transform duration-300 group-hover:-rotate-1 group-hover:scale-105 ${preset.iconClassName}`}
          >
            {renderPresetIcon(preset, "h-4 w-4")}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                    {preset.eyebrow}
                  </p>
                  {preset.badge && (
                    <Badge
                      variant={preset.badgeVariant ?? BadgeVariant.GRAY}
                      size={BadgeSize.XS}
                      className={`h-4 px-1.5 text-[10px] ${
                        preset.badgeVariant === BadgeVariant.AMBER ? "" : "bg-white/75"
                      }`}
                    >
                      {preset.badge}
                    </Badge>
                  )}
                </div>
                <p className="mt-0.5 truncate text-sm font-semibold text-gray-950">
                  {isBlankLoading ? "Creating blank table…" : preset.title}
                </p>
              </div>
              <ArrowRight
                className={`mt-1 h-4 w-4 shrink-0 text-gray-300 transition-all duration-300 group-hover:translate-x-1 ${preset.arrowClassName}`}
              />
            </div>

            <p className="mt-1.5 line-clamp-2 text-xs leading-4 text-gray-500">
              {preset.description}
            </p>
          </div>
        </div>
      </button>
    );
  };

  if (!visiblePresets.length) {
    return null;
  }

  return (
    <div className="max-h-[calc(86vh-5.5rem)] overflow-y-auto overscroll-contain bg-white px-4 pb-8 pt-4 sm:px-5 sm:pb-7">
      <div className="space-y-5">
        {aiPreset && <Section title="Start with AI">{renderFeaturedCard(aiPreset)}</Section>}

        {popularPresets.length > 0 && (
          <Section title="Popular presets">
            <div className="grid gap-2.5 sm:grid-cols-2">
              {popularPresets.map((preset, index) => renderCompactCard(preset, index))}
            </div>
          </Section>
        )}

        {buildYourselfPresets.length > 0 && (
          <Section title="Build yourself">
            <div className="grid gap-2.5 sm:grid-cols-2">
              {buildYourselfPresets.map((preset, index) =>
                renderCompactCard(preset, popularPresets.length + index),
              )}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
};

export default PresetGrid;
